"use client";
import { useState, FormEvent } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { User, Mail, Lock, Eye, EyeOff, UserPlus, Globe, CheckCircle } from "lucide-react";
import { signIn } from "next-auth/react";

function PasswordStrength({ password }: { password: string }) {
  const checks = [
    { label: "8+ characters", pass: password.length >= 8 },
    { label: "Uppercase letter", pass: /[A-Z]/.test(password) },
    { label: "Lowercase letter", pass: /[a-z]/.test(password) },
    { label: "Number", pass: /\d/.test(password) },
  ];
  const score = checks.filter((c) => c.pass).length;
  const strength = score <= 1 ? { label: "Weak", color: "bg-red-500", text: "text-red-600" }
    : score <= 2 ? { label: "Fair", color: "bg-amber-500", text: "text-amber-600" }
    : score <= 3 ? { label: "Good", color: "bg-blue-500", text: "text-blue-600" }
    : { label: "Strong", color: "bg-emerald-500", text: "text-emerald-600" };

  if (!password) return null;

  return (
    <div className="mt-2.5 space-y-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>Password strength</span>
        <span className={`text-xs font-medium ${strength.text}`}>{strength.label}</span>
      </div>
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all duration-300 ${i < score ? strength.color : "bg-slate-200"}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
        {checks.map((c) => (
          <div key={c.label} className="flex items-center gap-1.5">
            <CheckCircle size={11} className={c.pass ? "text-emerald-600" : "text-slate-400"} />
            <span className={`text-xs ${c.pass ? "text-slate-700" : "text-slate-400"}`}>{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SignupPage() {
  const router = useRouter();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [businessName, setBusinessName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const update = (field: string, value: string) => {
    setForm((p) => ({ ...p, [field]: value }));
    setFieldErrors((p) => ({ ...p, [field]: "" }));
  };

  function validate() {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = "Full name is required.";
    if (!form.email) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Enter a valid email address.";
    if (!form.password) errors.password = "Password is required.";
    else if (form.password.length < 8) errors.password = "Password must be at least 8 characters.";
    if (!form.confirmPassword) errors.confirmPassword = "Please confirm your password.";
    else if (form.password !== form.confirmPassword) errors.confirmPassword = "Passwords do not match.";
    if (!agreedToTerms) errors.terms = "You must agree to the Terms & Conditions.";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password, businessName }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Registration failed. Please try again.");
        return;
      }
      router.push("/dashboard");
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignUp() {
    setGoogleLoading(true);
    setError("");
    try {
      await signIn("google", { callbackUrl: "/login?google=1" });
    } catch {
      setError("Google sign-up failed. Please try again.");
      setGoogleLoading(false);
    }
  }

  const inputClass = (field: string) =>
    `w-full border rounded-xl py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors`;

  return (
    <div className="flex-1 auth-bg flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center font-bold text-sm text-white shadow-lg shadow-blue-600/30 group-hover:bg-blue-700 transition-colors">
              M
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ color: "var(--color-text-primary)" }}>Meridian KPI Platform</span>
          </Link>
        </div>

        <div className="rounded-2xl p-8 shadow-xl border" style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-elevated)" }}>
          <div className="mb-7">
            <h1 className="text-2xl font-bold mb-1.5" style={{ color: "var(--color-text-primary)" }}>Create your account</h1>
            <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>Start tracking your KPIs — free for 14 days</p>
          </div>

          {/* Google Sign-Up */}
          <button
            type="button"
            onClick={handleGoogleSignUp}
            disabled={loading || googleLoading}
            className="w-full flex items-center justify-center gap-3 border py-2.5 px-4 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-5 hover:bg-blue-50"
            style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-input)" }}
          >
            {googleLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-slate-300" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Connecting…
              </>
            ) : (
              <>
                <Globe size={17} className="text-slate-300" />
                Sign up with Google
              </>
            )}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} />
            <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>or sign up with email</span>
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} />
          </div>

          {/* Error banner */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-5 flex items-start gap-2">
              <span className="mt-0.5 shrink-0">⚠</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Full Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--color-text-muted)" }} />
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jane Smith"
                  className={`${inputClass("name")} pl-10 pr-4`}
                />
              </div>
              {fieldErrors.name && <p className="text-red-600 text-xs mt-1.5">{fieldErrors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--color-text-muted)" }} />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="jane@company.com"
                  className={`${inputClass("email")} pl-10 pr-4`}
                  style={{ backgroundColor: "var(--color-bg-input)", borderColor: fieldErrors.email ? "#ef4444" : "var(--color-border-input)", color: "var(--color-text-primary)" }}
                />
              </div>
              {fieldErrors.email && <p className="text-red-600 text-xs mt-1.5">{fieldErrors.email}</p>}
            </div>

            {/* Business Name (optional) */}
            <div>
              <label htmlFor="business" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                Business Name <span className="font-normal text-xs" style={{ color: "var(--color-text-muted)" }}>(optional)</span>
              </label>
              <input
                id="business"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Acme Corp"
                className="w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors"
                style={{ backgroundColor: "var(--color-bg-input)", borderColor: "var(--color-border-input)", color: "var(--color-text-primary)" }}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--color-text-muted)" }} />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                  placeholder="Min 8 characters"
                  className={`${inputClass("password")} pl-10 pr-11`}
                  style={{ backgroundColor: "var(--color-bg-input)", borderColor: fieldErrors.password ? "#ef4444" : "var(--color-border-input)", color: "var(--color-text-primary)" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors hover:text-blue-600"
                  style={{ color: "var(--color-text-muted)" }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {fieldErrors.password && <p className="text-red-600 text-xs mt-1.5">{fieldErrors.password}</p>}
              <PasswordStrength password={form.password} />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--color-text-muted)" }} />
                <input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={(e) => update("confirmPassword", e.target.value)}
                  placeholder="Repeat your password"
                  className={`${inputClass("confirmPassword")} pl-10 pr-11`}
                  style={{ backgroundColor: "var(--color-bg-input)", borderColor: fieldErrors.confirmPassword ? "#ef4444" : "var(--color-border-input)", color: "var(--color-text-primary)" }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors hover:text-blue-600"
                  style={{ color: "var(--color-text-muted)" }}
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <p className="text-red-600 text-xs mt-1.5">Passwords do not match.</p>
              )}
              {form.confirmPassword && form.password === form.confirmPassword && form.password.length >= 8 && (
                <p className="text-emerald-700 text-xs mt-1.5 flex items-center gap-1">
                  <CheckCircle size={11} /> Passwords match
                </p>
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="pt-1">
              <div className="flex items-start gap-2.5">
                <input
                  id="terms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    setFieldErrors((p) => ({ ...p, terms: "" }));
                  }}
                  className="w-4 h-4 mt-0.5 rounded cursor-pointer accent-blue-600"
                />
                <label htmlFor="terms" className="text-sm cursor-pointer leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                  I agree to the{" "}
                  <Link href="/terms" target="_blank" className="text-blue-600 hover:text-blue-700 transition-colors">Terms & Conditions</Link>{" "}
                  and{" "}
                  <Link href="/privacy" target="_blank" className="text-blue-600 hover:text-blue-700 transition-colors">Privacy Policy</Link>
                </label>
              </div>
              {fieldErrors.terms && <p className="text-red-600 text-xs mt-1.5 ml-6">{fieldErrors.terms}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed py-2.5 rounded-xl font-semibold text-sm text-white transition-all shadow-lg shadow-blue-600/20 mt-1"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Creating account…
                </>
              ) : (
                <>
                  Create Account
                  <UserPlus size={15} />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm mt-5" style={{ color: "var(--color-text-muted)" }}>
          Already have an account?{" "}
          <Link href="/login" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
